import React, { useState } from 'react'
import { createRoot } from 'react-dom/client'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { template, truncate } from 'lodash'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Icon } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { Provider } from 'react-redux'
import { MemoryRouter } from 'react-router'
import AppStore from 'store/AppStore'
import { Theme } from 'utils/constants/Theme'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { NewTable } from 'components/datatable/NewTable'

// Popup d'alerte : nombre de cas en titre, puis tableau détaillant chaque avertissement (fermeture par la croix du titre ou Terminer)
const WarningDetailsDialog = ({
    message = '',
    headers = [],
    rows = [],
    onClosed = () => { },
}) => {
    const [open, setOpen] = useState(true)

    return (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth='lg' fullWidth TransitionProps={{ onExited: onClosed }}>
            <DefaultDialogTitle
                title={(
                    <Grid container spacing={1} alignItems='center' wrap='nowrap'>
                        <Icon sx={{ color: 'orange' }}>warning</Icon>
                        <Grid size='grow'>{message}</Grid>
                    </Grid>
                )}
                onClose={() => setOpen(false)}
            />
            <DialogContent sx={{ paddingTop: '0.5rem !important' }}>
                <Card>
                    <CardContent>
                        <NewTable
                            rows={rows}
                            headers={headers}
                            rowsPerPageOptions={[{ value: 1000, label: '1000' }]}
                        />
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={() => setOpen(false)}>{i18n.finish}</Button>
            </DialogActions>
        </Dialog>
    )
}

WarningDetailsDialog.propTypes = {
    message: PropTypes.string,
    headers: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.string,
    })),
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    onClosed: PropTypes.func,
}

// ouverture depuis une action (hors de l'arbre React) : la popup est montée puis démontée à la fermeture
const openWarningDetailsDialog = ({ message, headers, rows }) => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)
    const onClosed = () => {
        root.unmount()
        container.remove()
    }
    root.render(
        // contextes de l'application recréés : la popup est montée hors de App (NewTable utilise useLocation)
        <Provider store={AppStore}>
            <MemoryRouter initialEntries={[window.location.hash.replace(/^#/, '') || '/']}>
                <ThemeProvider theme={Theme}>
                    <WarningDetailsDialog message={message} headers={headers} rows={rows} onClosed={onClosed} />
                </ThemeProvider>
            </MemoryRouter>
        </Provider>,
    )
}

const PARAMETER_LABEL_MAX_LENGTH = 30

const truncatedCell = (text = '', length) => (text.length > length ? { value: truncate(text, { length }), tooltip: text } : { value: text })

// paramètres sans unité exportés avec l'unité "Sans objet" (export EDILABO)
const openDefaultUnitWarningsDialog = (warnings = []) => openWarningDetailsDialog({
    message: template(warnings.length > 1 ? i18n.edilaboDefaultUnitWarnings : i18n.edilaboDefaultUnitWarning)({ count: warnings.length }),
    headers: [
        { key: 'parameterCode', value: i18n.parameterCode },
        { key: 'parameterName', value: i18n.parameter },
        { key: 'message', value: i18n.warningMessage },
    ],
    rows: warnings.map(({ parameterCode, parameterName = '', message = '' }) => {
        // libellé du paramètre tronqué à PARAMETER_LABEL_MAX_LENGTH caractères, message complet tronqué à MESSAGE_MAX_LENGTH
        // texte complet en infobulle quand il est tronqué
        return {
            parameterCode,
            parameterName: truncatedCell(parameterName, PARAMETER_LABEL_MAX_LENGTH),
            message: truncatedCell(message),
        }
    }),
})

export default WarningDetailsDialog
export {
    openWarningDetailsDialog,
    openDefaultUnitWarningsDialog,
}
